import { createSlice, createAction, PayloadAction } from '@reduxjs/toolkit';
import { Middleware } from '@reduxjs/toolkit';
import { AUTH_STORAGE_KEYS } from '../../constant/constant';
import { NavigateFunction } from 'react-router-dom';
import { VerificationState } from '../../../features/auth/types/account';

export interface AuthState {
  token: string;
  isAuthenticated: boolean;
  tokenExpiresAt: number | null;
  userId: string;
  authFlow: 'login' | 'signup' | null;
  verification: VerificationState;
  isSigningIn: boolean;
  isSendingCode: boolean;
  isVerifyingCode: boolean;
  isLoading: boolean;

  error: {
    hasError: boolean;
    message: string;
    source?: string | null;
    timestamp: number;
  } | null;
  success: {
    isSuccess: boolean;
    message: string;
    timestamp: number;
  } | null;
  sessionExpired: boolean;
  userStatus: string;
}

// Initial state
const hydrateAuthFromStorage = (): {
  token: string;
  isAuthenticated: boolean;
  tokenExpiresAt: number | null;
  userId: string;
} => {
  try {
    const storedAuth = localStorage.getItem(AUTH_STORAGE_KEYS.AUTH);
    if (storedAuth) {
      const parsedAuth = JSON.parse(storedAuth);

      if (parsedAuth.expiresAt && parsedAuth.expiresAt < Date.now()) {
        localStorage.removeItem(AUTH_STORAGE_KEYS.AUTH);
        return {
          token: '',
          isAuthenticated: false,
          tokenExpiresAt: null,
          userId: '',
        };
      }
      return {
        token: parsedAuth.token || '',
        isAuthenticated: !!parsedAuth.token,
        tokenExpiresAt: parsedAuth.expiresAt,
        userId: parsedAuth.userId || '',
      };
    }
  } catch (e) {
    console.error('Failed to parse auth from localStorage', e);
    localStorage.removeItem(AUTH_STORAGE_KEYS.AUTH);
  }
  return {
    token: '',
    isAuthenticated: false,
    tokenExpiresAt: null,
    userId: '',
  };
};

const hydratedAuth = hydrateAuthFromStorage();

const initialState: AuthState = {
  ...hydratedAuth,
  authFlow: null,
  verification: {
    stage: 'initial',
    phoneNumber: '',
    email: '',
    userId: '',
    phoneVerified: false,
    emailVerified: false,
  },
  isSigningIn: false,
  isSendingCode: false,
  isVerifyingCode: false,
  isLoading: false,
  error: null,
  success: null,
  sessionExpired: false,
  userStatus: ''
};

export const sessionExpired = createAction('auth/sessionExpired');

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setRequestStatus: (
      state,
      action: PayloadAction<{
        isLoading?: boolean;
        isSendingCode?: boolean;
        isVerifyingCode?: boolean;
      }>
    ) => {
      if (action.payload.isLoading !== undefined) {
        state.isLoading = action.payload.isLoading;
      }
      if (action.payload.isSendingCode !== undefined) {
        state.isSendingCode = action.payload.isSendingCode;
      }
      if (action.payload.isVerifyingCode !== undefined) {
        state.isVerifyingCode = action.payload.isVerifyingCode;
      }
    },

    setAuthenticated: (
      state,
      action: PayloadAction<{
        token: string;
        userId: string;
        ttl: string;
      }>
    ) => {
      state.isAuthenticated = true;
      state.token = action.payload.token;
      state.userId = action.payload.userId;

      const expiresAt = new Date();
      expiresAt.setSeconds(expiresAt.getSeconds() + parseInt(action.payload.ttl, 10));
      state.tokenExpiresAt = Number(expiresAt.toISOString());
    },

    logout: (state) => {
      state.isAuthenticated = false;
      state.token = '';
      state.userId = '';
      state.tokenExpiresAt = null;
      state.verification = initialState.verification;
      state.authFlow = null;
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = {
        hasError: true,
        message: action.payload,
        timestamp: Date.now(),
      };
    },

    clearError: (state) => {
      state.error = null;
    },

    setSuccess: (state, action: PayloadAction<{ message: string }>) => {
      state.success = {
        isSuccess: true,
        message: action.payload.message,
        timestamp: Date.now(),
      };
    },

    clearSuccess: (state) => {
      state.success = null;
    },

    setVerificationSent: (
      state,
      action: PayloadAction<{
        phoneNumber: string;
        email?: string;
        userId: string;
        stage?: 'phoneVerification' | 'initial' | 'emailInitiation';
      }>
    ) => {
      state.verification = {
        ...state.verification,
        stage: action.payload.stage || 'phoneVerification',
        phoneNumber: action.payload.phoneNumber,
        email: action.payload.email || '',
        userId: action.payload.userId,
        phoneVerified: false,
        emailVerified: false,
      };
    },

    setEmailVerificationSent: (
      state,
      action: PayloadAction<{
        email: string;
        stage: 'emailVerification';
      }>
    ) => {
      state.verification = {
        ...state.verification,
        stage: action.payload.stage,
        email: action.payload.email,
      };
    },

    setPhoneVerified: (state) => {
      state.verification = {
        ...state.verification,
        phoneVerified: true,
        stage: 'emailInitiation',
      };
    },

    setEmailVerified: (state) => {
      state.verification = {
        ...state.verification,
        emailVerified: true,
        stage: 'complete',
      };
    },

    /**
     * Used when phone verification now completes signup (email OTP removed).
     * Sets both flags and jumps to 'complete' in a single dispatch to avoid
     * a momentary flash of the emailInitiation step.
     */
    setSignupComplete: (state) => {
      state.verification = {
        ...state.verification,
        phoneVerified: true,
        emailVerified: true,
        stage: 'complete',
      };
    },

    resetVerification: (state) => {
      state.verification = initialState.verification;
    },

    setSessionExpired: (state) => {
      state.sessionExpired = true;
      state.isAuthenticated = false;
      state.token = '';
      state.tokenExpiresAt = null;
      state.authFlow = null;
      localStorage.removeItem(AUTH_STORAGE_KEYS.AUTH);
      if (!state.error) {
        state.error = {
          hasError: true,
          message: 'Your session has expired. Please sign in again.',
          source: 'session',
          timestamp: Date.now()
        }
      }
    },

    clearSessionExpiredFlag: (state) => {
      state.sessionExpired = false;
    },
    resetAll: (state) => {
      Object.assign(state, initialState);
    },
    setAuthFlow: (state, action: PayloadAction<'login' | 'signup' | null>) => {
      state.authFlow = action.payload;
    },
    setUserStatus: (state, action) => {
      state.userStatus = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(sessionExpired, (state) => {
      state.sessionExpired = true;
      state.isAuthenticated = false;
      state.token = '';
      state.tokenExpiresAt = null;
      state.authFlow = null;
      localStorage.removeItem(AUTH_STORAGE_KEYS.AUTH);

      if (!state.error) {
        state.error = {
          hasError: true,
          message: 'Your session has expired. Please sign in again.',
          source: 'session',
          timestamp: Date.now()
        };
      }
    });
  }
});

export const authNavigationMiddleware =
  (navigate: NavigateFunction): Middleware =>
    () =>
      (next) =>
        (action: { type: string } | unknown) => {
          if (action && typeof action === 'object' && 'type' in action) {
            if (action.type === 'auth/sessionExpired') {
              navigate('/auth#login', { replace: true });
            }
          }

          return next(action);
        };

export const authPersistenceMiddleware: Middleware = (store) => (next) => (action: { type: string } | unknown) => {
  const result = next(action);

  if (action && typeof action === 'object' && 'type' in action) {
    if (
      action.type === 'auth/setAuthenticated' ||
      action.type === 'auth/logout'
    ) {
      const state = store.getState();
      const authState = state.auth;

      if (authState.isAuthenticated && authState.token) {
        localStorage.setItem(
          AUTH_STORAGE_KEYS.AUTH,
          JSON.stringify({
            token: authState.token,
            userId: authState.userId,
            expiresAt: authState.tokenExpiresAt
          })
        );
      } else {
        localStorage.removeItem(AUTH_STORAGE_KEYS.AUTH);
      }
    }
  }

  return result;
};

export const {
  setVerificationSent,
  setPhoneVerified,
  setEmailVerified,
  setEmailVerificationSent,
  setSignupComplete,
  resetVerification,
  setAuthenticated,
  setRequestStatus,
  setError,
  clearError,
  setSuccess,
  clearSuccess,
  logout,
  setSessionExpired,
  clearSessionExpiredFlag,
  resetAll,
  setAuthFlow,
  setUserStatus
} = authSlice.actions;

export default authSlice.reducer;
